import { object } from "prop-types";
import React, { useEffect, useState } from "react";
import useViewPort from "../../../../hook/useViewPort";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { getStoryBySection } from "../../../../../api/utils";
import SeriesPageKlipitComponent from "../SeriesPageKlipitComponent";
import SeriesPageSummaryComponent from "../SeriesPageSummaryComponent";
import SeriesPageSimilarItemsComponent from "../SeriesPageSimilarItemsComponent";
import SeriesPageEpisodeListingComponent from "../SeriesPageEpisodeListingComponent";
import SeriesPageMainTabButtonsComponent from "../SeriesPageMainTabButtonsComponent";
import "../../../../components/pages/section.m.css";
import "../OneBigThreeSmallSectionLayout/one-big-three-small-section-layout.m.css";
import "./series-page-tab-component.m.css";

const SeriesPageTabComponent = ({ data }) => {
  const [activeTab, setActiveTab] = useState("jaksot");
  const [selectedButton, setSelectedButton] = useState(0);
  const [similarSections, setSimilarSections] = useState([]);
  const [parentOfSimilarSection, setParentOfSimilarSection] = useState(null);
  const [klipitStories, setKlipitStories] = useState([]);
  const [activeSectionStories, setActiveSectionStories] = useState(data?.firstSectionStories);
  const [isAjaxLoading, setIsAjaxLoading] = useState(false);
  const [sectionName, setSectionName] = useState("");

  // init data for "similar sections" tab
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const currentPath = get(qtState, ["currentPath"], "");


  let genretSlug = null;
  if (currentPath.startsWith("/lammas")) {
    genretSlug = data?.lammasSimilarSectionSlug ?? null;
  } else {
    genretSlug = data?.deiSimilarSectionSlug ?? null;
  }

  const showHideSamanlaisiaOhjelmia = data?.collection?.metadata?.["similar-shows"]?.[0]?.name ?? null;

  // init other misc data
  const strCollectionSummery = data?.collection?.summary ?? null;
  const strCurSeriesSlug = data?.collection?.slug ?? null;
  const stateConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const defaultImage= get(stateConfig, ["publisher-attributes", "defaultImageKey"], "");
  const contentType = data?.collection?.metadata["content-type"]?.[0]?.name || null;
  const behindPaywall = data?.collection?.metadata["behind-paywall"]?.[0]?.name || null;
  const strCollectionSortOrder = data?.collection?.metadata?.["listing-order"]?.[0]?.name.toLowerCase() ?? null;
  const { viewports } = useViewPort();

  // events and hooks
  const handleButtonClick = (tab) => {
    setActiveTab(tab);
  };

  const handleFetchSimilarItems = async (tab) => {
    setActiveTab(tab);

    if (genretSlug && showHideSamanlaisiaOhjelmia === "Päällä") {
      setIsAjaxLoading(true);

      try {
        // fetch all genres (horror/comedy/drama etc.)
        const genretResponse = await fetch(`/api/v1/collections/${genretSlug}`);
        const jsonGenretData = await genretResponse.json();

        const genretCollectionItems = jsonGenretData?.items?.filter(
          (genretItem) => genretItem?.type === "collection"
        ) || [];

        if (!genretCollectionItems.length) { return; }

        let otherSimilarSeriesItems = [];
        for (const genretItem of genretCollectionItems) {
          try {
              // fetch items of each genre
              const response = await fetch(`/api/v1/collections/${genretItem.slug}`);
              if (!response.ok) {
                  throw new Error(`Failed to fetch ${genretItem.slug}: ${response.statusText}`);
              }

              const jsonData = await response.json();
              const genretSeriesItems = jsonData?.items?.filter(
                (seriesItem) => seriesItem?.type === "collection"
              ) || [];

              if (!genretSeriesItems.length) { continue; }

              // check if current series page belongs to this genret
              const matchedSeriesItem = genretSeriesItems.find(item => item?.slug === strCurSeriesSlug);

              if (matchedSeriesItem) {
                // lets collect all other series except the current series page
                otherSimilarSeriesItems = genretSeriesItems?.filter(
                  (seriesItem) => seriesItem?.slug !== strCurSeriesSlug
                )?.slice(0, 5) || [];
                setParentOfSimilarSection(genretItem);
                break;
              }
          } catch (error) {
              console.error(`Error fetching ${genretItem.slug}:`, error);
          }
        }

        if (otherSimilarSeriesItems.length) {
          setSimilarSections(otherSimilarSeriesItems);
        }
      } catch (error) {
        console.error("Error fetching data from external API:", error);
      } finally {
        setIsAjaxLoading(false);
      }
    }
  };

  const handleFetchKlipitItems = async (tab, subSections) => {
    setIsAjaxLoading(true);
    setActiveTab(tab);

    const filteredSection = subSections.find(section => section.slug?.includes("klipit")) ?? null;
    const sectionID = filteredSection?.metadata?.section?.[0]?.id || null;

    try {
      if (sectionID) {
        const klipitStories = await getStoryBySection(sectionID);
        setKlipitStories(klipitStories);
      }
    } catch (error) {
      console.error("Failed to fetch klipit stories:", error);
    } finally {
      setIsAjaxLoading(false);
    }
  };

  const handleFetchSubSectionItems = async (sectionID, sectionName, index) => {
    setSectionName(sectionName);
    setSelectedButton(index);

    if (sectionID) {
      const stories = await getStoryBySection(sectionID);

      // if meta-field is oldest-to-newset, need to reverse the stories order, since default is desc by published-date
      if (strCollectionSortOrder == "vanhin ensin") {
        stories.reverse();
      }

      setActiveSectionStories(stories);
    }
  };

  const handleSortActSectionStories = () => {
    setActiveSectionStories([...activeSectionStories].reverse());
  };

  return (
    <React.Fragment>
      <SeriesPageMainTabButtonsComponent
        activeTab={activeTab}
        selectedButton={selectedButton}
        setSelectedButton={setSelectedButton}
        handleButtonClick={handleButtonClick}
        handleFetchKlipitItems={handleFetchKlipitItems}
        handleFetchSimilarItems={handleFetchSimilarItems}
        genretSlug={genretSlug}
        showHideSamanlaisiaOhjelmia={showHideSamanlaisiaOhjelmia}
        currentPath={currentPath}
        data={data}
      />

      {activeTab !== "samanlaisia" && activeTab !== "tarkemmat" && activeTab !== "klipit" && (
        <SeriesPageEpisodeListingComponent
          data={data}
          currentPath={currentPath}
          selectedButton={selectedButton}
          handleFetchSubSectionItems={handleFetchSubSectionItems}
          activeSectionStories={activeSectionStories}
          handleSortActSectionStories={handleSortActSectionStories}
          behindPaywall={behindPaywall}
          contentType={contentType}
        />
      )}

      {activeTab === "tarkemmat" && (
        <SeriesPageSummaryComponent
          strCollectionSummery={strCollectionSummery} />
      )}

      {activeTab === "klipit" && (
        <SeriesPageKlipitComponent
          klipitStories={klipitStories}
          isAjaxLoading={isAjaxLoading}
          behindPaywall={behindPaywall}
          contentType={contentType}
        />
      )}

      {activeTab === "samanlaisia" && (
        <SeriesPageSimilarItemsComponent
          isAjaxLoading={isAjaxLoading}
          similarSections={similarSections}
          parentOfSimilarSection={parentOfSimilarSection}
          viewports={viewports}
          stateConfig={stateConfig}
          defaultImage={defaultImage}
        />
      )}
    </React.Fragment>
  );
}

SeriesPageTabComponent.propTypes = {
  data: object
};

export { SeriesPageTabComponent };
